/**
 * DSL Function Metadata Service
 * Provides comprehensive function information for autocomplete and documentation
 */

// Complete DSL function metadata with categories, signatures, and examples
export const DSL_FUNCTION_METADATA = {
  // Arithmetic Operations
  add: {
    category: 'arithmetic',
    signature: 'add(a, b, ...)',
    params: ['a', 'b'],
    variadic: true,
    description: 'Addition of two or more numbers',
    example: 'eval(add(2, 3)) → 5',
    latex: 'a + b + ...'
  },
  sub: {
    category: 'arithmetic',
    signature: 'sub(a, b)',
    params: ['a', 'b'],
    description: 'Subtraction of two numbers',
    example: 'eval(sub(5, 3)) → 2',
    latex: 'a - b'
  },
  mul: {
    category: 'arithmetic',
    signature: 'mul(a, b, ...)',
    params: ['a', 'b'],
    variadic: true,
    description: 'Multiplication of two or more numbers',
    example: 'eval(mul(2, 3, 4)) → 24',
    latex: 'a \\cdot b \\cdot ...'
  },
  div: {
    category: 'arithmetic',
    signature: 'div(a, b)',
    params: ['a', 'b'],
    description: 'Division of two numbers',
    example: 'eval(div(6, 2)) → 3',
    latex: '\\frac{a}{b}'
  },
  pow: {
    category: 'arithmetic',
    signature: 'pow(base, exponent)',
    params: ['base', 'exponent'],
    description: 'Exponentiation',
    example: 'eval(pow(2, 3)) → 8',
    latex: 'base^{exponent}'
  },
  neg: {
    category: 'arithmetic',
    signature: 'neg(x)',
    params: ['x'],
    description: 'Negation',
    example: 'eval(neg(5)) → -5',
    latex: '-x'
  },
  sqrt: {
    category: 'arithmetic',
    signature: 'sqrt(x)',
    params: ['x'],
    description: 'Square root',
    example: 'eval(sqrt(9)) → 3',
    latex: '\\sqrt{x}'
  },
  round: {
    category: 'arithmetic',
    signature: 'round(x)',
    params: ['x'],
    description: 'Round to nearest integer',
    example: 'eval(round(3.7)) → 4',
    latex: '\\text{round}(x)'
  },

  // Trigonometric Functions
  sin: {
    category: 'trigonometric',
    signature: 'sin(x)',
    params: ['x'],
    description: 'Sine function',
    example: 'eval(sin(div(pi, 2))) → 1',
    latex: '\\sin(x)'
  },
  cos: {
    category: 'trigonometric',
    signature: 'cos(x)',
    params: ['x'],
    description: 'Cosine function',
    example: 'eval(cos(pi)) → -1',
    latex: '\\cos(x)'
  },
  tan: {
    category: 'trigonometric',
    signature: 'tan(x)',
    params: ['x'],
    description: 'Tangent function',
    example: 'eval(tan(div(pi, 4))) → 1',
    latex: '\\tan(x)'
  },
  sec: {
    category: 'trigonometric',
    signature: 'sec(x)',
    params: ['x'],
    description: 'Secant function (1/cos(x))',
    example: 'eval(sec(0)) → 1',
    latex: '\\sec(x)'
  },
  csc: {
    category: 'trigonometric',
    signature: 'csc(x)',
    params: ['x'],
    description: 'Cosecant function (1/sin(x))',
    example: 'eval(csc(div(pi, 2))) → 1',
    latex: '\\csc(x)'
  },
  cot: {
    category: 'trigonometric',
    signature: 'cot(x)',
    params: ['x'],
    description: 'Cotangent function (1/tan(x))',
    example: 'eval(cot(div(pi, 4))) → 1',
    latex: '\\cot(x)'
  },
  asin: {
    category: 'trigonometric',
    signature: 'asin(x)',
    params: ['x'],
    description: 'Arcsine function',
    example: 'eval(asin(div(1, 2))) → π/6',
    latex: '\\arcsin(x)'
  },
  acos: {
    category: 'trigonometric',
    signature: 'acos(x)',
    params: ['x'],
    description: 'Arccosine function',
    example: 'eval(acos(0)) → π/2',
    latex: '\\arccos(x)'
  },
  atan: {
    category: 'trigonometric',
    signature: 'atan(x)',
    params: ['x'],
    description: 'Arctangent function',
    example: 'eval(atan(1)) → π/4',
    latex: '\\arctan(x)'
  },

  // Exponential and Logarithmic
  exp: {
    category: 'exponential',
    signature: 'exp(x)',
    params: ['x'],
    description: 'Exponential function (e^x)',
    example: 'eval(exp(1)) → e',
    latex: 'e^{x}'
  },
  log: {
    category: 'exponential',
    signature: 'log(x)',
    params: ['x'],
    description: 'Natural logarithm',
    example: 'eval(log(e)) → 1',
    latex: '\\ln(x)'
  },

  // Calculus Functions
  diff: {
    category: 'calculus',
    signature: 'diff(expression, variable)',
    params: ['expression', 'variable'],
    description: 'Derivative with respect to variable',
    example: 'eval(diff(pow(x, 2), x)) → 2x',
    latex: '\\frac{d}{dx}[expression]'
  },
  partialdiff: {
    category: 'calculus',
    signature: 'partialdiff(expression, variable)',
    params: ['expression', 'variable'],
    description: 'Partial derivative',
    example: 'eval(partialdiff(mul(x, y), x)) → y',
    latex: '\\frac{\\partial}{\\partial x}[expression]'
  },
  integrate: {
    category: 'calculus',
    signature: 'integrate(expression, variable)',
    params: ['expression', 'variable'],
    description: 'Indefinite integral',
    example: 'integrate(pow(x, 2), x)',
    latex: '\\int expression \\, dx'
  },
  limit: {
    category: 'calculus',
    signature: 'limit(expression, variable, value)',
    params: ['expression', 'variable', 'value'],
    description: 'Limit of expression as variable approaches value',
    example: 'eval(limit(div(sin(x), x), x, 0)) → 1',
    latex: '\\lim_{variable \\to value} expression'
  },
  jacobian: {
    category: 'calculus',
    signature: 'jacobian(vector_function, variables)',
    params: ['vector_function', 'variables'],
    description: 'Jacobian matrix of vector function',
    example: 'jacobian(vector(mul(x,y), add(x,y)), vector(x,y))',
    latex: 'J = \\begin{bmatrix} \\frac{\\partial f_i}{\\partial x_j} \\end{bmatrix}'
  },
  hessian: {
    category: 'calculus',
    signature: 'hessian(function, variables)',
    params: ['function', 'variables'],
    description: 'Hessian matrix of second partial derivatives',
    example: 'hessian(mul(pow(x, 2), y), vector(x, y))',
    latex: 'H = \\begin{bmatrix} \\frac{\\partial^2 f}{\\partial x_i \\partial x_j} \\end{bmatrix}'
  },

  // Pedagogical Calculus Functions
  product_rule_diff: {
    category: 'pedagogical',
    signature: 'product_rule_diff(f, g, variable)',
    params: ['f', 'g', 'variable'],
    description: 'Shows product rule application step by step',
    example: 'product_rule_diff(mul(x, sin(x)), x)',
    latex: '\\frac{d}{dx}[fg] = f\'g + fg\''
  },
  sum_rule_diff: {
    category: 'pedagogical',
    signature: 'sum_rule_diff(f, g, variable)',
    params: ['f', 'g', 'variable'],
    description: 'Shows sum rule application step by step',
    example: 'sum_rule_diff(add(pow(x, 2), log(x)), x)',
    latex: '\\frac{d}{dx}[f+g] = f\' + g\''
  },
  chain_rule_diff: {
    category: 'pedagogical',
    signature: 'chain_rule_diff(outer, inner, variable)',
    params: ['outer', 'inner', 'variable'],
    description: 'Shows chain rule application step by step',
    example: 'chain_rule_diff(tan(pow(x, 3)), x)',
    latex: '\\frac{d}{dx}[f(g(x))] = f\'(g(x)) \\cdot g\'(x)'
  },
  quotient_rule_diff: {
    category: 'pedagogical',
    signature: 'quotient_rule_diff(numerator, denominator, variable)',
    params: ['numerator', 'denominator', 'variable'],
    description: 'Shows quotient rule application step by step',
    example: 'quotient_rule_diff(div(x, sin(x)), x)',
    latex: '\\frac{d}{dx}[\\frac{f}{g}] = \\frac{f\'g - fg\'}{g^2}'
  },

  // Linear Algebra
  vector: {
    category: 'linear_algebra',
    signature: 'vector(elements...)',
    params: ['elements'],
    variadic: true,
    description: 'Create a column vector',
    example: 'vector(1, 2, 3)',
    latex: '\\begin{bmatrix} 1 \\\\ 2 \\\\ 3 \\end{bmatrix}'
  },
  covector: {
    category: 'linear_algebra',
    signature: 'covector(elements...)',
    params: ['elements'],
    variadic: true,
    description: 'Create a row vector',
    example: 'covector(1, 2, 3)',
    latex: '\\begin{bmatrix} 1 & 2 & 3 \\end{bmatrix}'
  },
  matrix: {
    category: 'linear_algebra',
    signature: 'matrix(rows...)',
    params: ['rows'],
    variadic: true,
    description: 'Create a matrix from row vectors',
    example: 'matrix(covector(1, 2), covector(3, 4))',
    latex: '\\begin{bmatrix} 1 & 2 \\\\ 3 & 4 \\end{bmatrix}'
  },
  det: {
    category: 'linear_algebra',
    signature: 'det(matrix)',
    params: ['matrix'],
    description: 'Matrix determinant',
    example: 'eval(det(matrix(covector(1,2), covector(3,4)))) → -2',
    latex: '\\det(A)'
  },
  transpose: {
    category: 'linear_algebra',
    signature: 'transpose(matrix)',
    params: ['matrix'],
    description: 'Matrix transpose',
    example: 'eval(transpose(matrix(covector(1, 2), covector(3, 4))))',
    latex: 'A^T'
  },
  inner: {
    category: 'linear_algebra',
    signature: 'inner(u, v)',
    params: ['u', 'v'],
    description: 'Inner product of vectors',
    example: 'eval(inner(covector(1,1), covector(1,1))) → 2',
    latex: '\\langle u, v \\rangle'
  },
  cross: {
    category: 'linear_algebra',
    signature: 'cross(u, v)',
    params: ['u', 'v'],
    description: 'Cross product of 3D vectors',
    example: 'eval(cross(vector(1, 0, 0), vector(0, 1, 0))) → [0, 0, 1]',
    latex: 'u \\times v'
  },
  norm: {
    category: 'linear_algebra',
    signature: 'norm(vector)',
    params: ['vector'],
    description: 'Vector norm (magnitude)',
    example: 'eval(norm(vector(3, 4))) → 5',
    latex: '\\|v\\|'
  },
  identity_matrix: {
    category: 'linear_algebra',
    signature: 'identity_matrix(n)',
    params: ['n'],
    description: 'Create n×n identity matrix',
    example: 'identity_matrix(2)',
    latex: 'I_n'
  },

  // Matrix Row Operations
  row_op: {
    category: 'linear_algebra',
    signature: 'row_op(matrix, operation)',
    params: ['matrix', 'operation'],
    description: 'Apply elementary row operation to matrix',
    example: 'row_op(M, swap(row(1), row(2)))',
    latex: 'Elementary Row Operation',
    subOperations: {
      swap: {
        signature: 'swap(row(i), row(j))',
        description: 'Swap rows i and j',
        example: 'row_op(M, swap(row(1), row(3)))',
        latex: 'R_i \\leftrightarrow R_j'
      },
      scale: {
        signature: 'scale(row(i), k)',
        description: 'Scale row i by factor k',
        example: 'row_op(M, scale(row(2), div(1, 3)))',
        latex: 'R_i \\leftarrow k \\cdot R_i'
      },
      add: {
        signature: 'add(row(i), mul(k, row(j)))',
        description: 'Add k times row j to row i',
        example: 'row_op(M, add(row(3), mul(-7, row(1))))',
        latex: 'R_i \\leftarrow R_i + k \\cdot R_j'
      }
    }
  },

  // Special Functions
  eval: {
    category: 'special',
    signature: 'eval(expression)',
    params: ['expression'],
    description: 'Evaluate expression numerically',
    example: 'eval(add(2, 3)) → 5',
    latex: 'expression'
  },
  eq: {
    category: 'special',
    signature: 'eq(left | right)',
    params: ['left', 'right'],
    description: 'Create an equation',
    example: 'eq(add(x, 2) | 5)',
    latex: 'left = right'
  },
  chain: {
    category: 'special',
    signature: 'chain(left | right)',
    params: ['left', 'right'],
    description: 'Create an implication (long rightward arrow)',
    example: 'chain(add(x, 2) | 5)',
    latex: 'left \\Longrightarrow right'
  },
  poly: {
    category: 'special',
    signature: 'poly(variable, ...coefficients)',
    params: ['variable', 'coefficients'],
    variadic: true,
    description: 'Create polynomial with given coefficients',
    example: 'poly(x, 1, 2, neg(5), 1) → x³ + 2x² - 5x + 1',
    latex: 'a_n x^n + ... + a_1 x + a_0'
  },

  // Abstract Algebra (GF(2) operations)
  gf2_add: {
    category: 'abstract_algebra',
    signature: 'gf2_add(a, b)',
    params: ['a', 'b'],
    description: 'Addition in GF(2) field',
    example: 'gf2_add(1, 1) → 0',
    latex: 'a \\oplus b'
  },
  gf2_mul: {
    category: 'abstract_algebra',
    signature: 'gf2_mul(a, b)',
    params: ['a', 'b'],
    description: 'Multiplication in GF(2) field',
    example: 'gf2_mul(1, 1) → 1',
    latex: 'a \\odot b'
  }
};

// Function categories for organization
export const FUNCTION_CATEGORIES = {
  arithmetic: {
    name: 'Arithmetic',
    description: 'Basic arithmetic operations',
    color: '#3B82F6'
  },
  trigonometric: {
    name: 'Trigonometric',
    description: 'Trigonometric functions',
    color: '#10B981'
  },
  exponential: {
    name: 'Exponential',
    description: 'Exponential and logarithmic functions',
    color: '#F59E0B'
  },
  calculus: {
    name: 'Calculus',
    description: 'Derivatives, integrals, and limits',
    color: '#EF4444'
  },
  linear_algebra: {
    name: 'Linear Algebra',
    description: 'Vector and matrix operations',
    color: '#8B5CF6'
  },
  special: {
    name: 'Special',
    description: 'Special utility functions',
    color: '#6B7280'
  },
  abstract_algebra: {
    name: 'Abstract Algebra',
    description: 'Abstract algebraic structures',
    color: '#EC4899'
  }
};

// Mathematical constants
export const DSL_CONSTANTS = {
  pi: { value: 'π', description: 'Pi constant', latex: '\\pi' },
  e: { value: 'e', description: 'Euler\'s number', latex: 'e' },
  tau: { value: '2π', description: 'Tau constant', latex: '\\tau' },
  phi: { value: '(1+√5)/2', description: 'Golden ratio', latex: '\\phi' },
  sqrt2: { value: '√2', description: 'Square root of 2', latex: '\\sqrt{2}' },
  inf: { value: '∞', description: 'Infinity', latex: '\\infty' },
  nan: { value: 'NaN', description: 'Not a number', latex: '\\text{NaN}' },
  true: { value: 'true', description: 'Boolean true', latex: '\\text{true}' },
  false: { value: 'false', description: 'Boolean false', latex: '\\text{false}' }
};

/**
 * Get function metadata by name
 */
export const getFunctionMetadata = (functionName) => {
  return DSL_FUNCTION_METADATA[functionName] || null;
};

/**
 * Get all functions in a category
 */
export const getFunctionsByCategory = (category) => {
  return Object.entries(DSL_FUNCTION_METADATA)
    .filter(([_, metadata]) => metadata.category === category)
    .map(([name, metadata]) => ({ name, ...metadata }));
};

/**
 * Search functions by name or description
 */
export const searchFunctions = (query, limit = 10) => {
  if (!query || query.length < 1) return [];
  
  const lowerQuery = query.toLowerCase();
  const results = [];
  
  // Exact name matches first
  Object.entries(DSL_FUNCTION_METADATA).forEach(([name, metadata]) => {
    if (name.toLowerCase() === lowerQuery) {
      results.push({ name, ...metadata, score: 100 });
    }
  });
  
  // Name starts with query
  Object.entries(DSL_FUNCTION_METADATA).forEach(([name, metadata]) => {
    if (name.toLowerCase().startsWith(lowerQuery) && !results.find(r => r.name === name)) {
      results.push({ name, ...metadata, score: 90 });
    }
  });
  
  // Name contains query
  Object.entries(DSL_FUNCTION_METADATA).forEach(([name, metadata]) => {
    if (name.toLowerCase().includes(lowerQuery) && !results.find(r => r.name === name)) {
      results.push({ name, ...metadata, score: 80 });
    }
  });
  
  // Description contains query
  Object.entries(DSL_FUNCTION_METADATA).forEach(([name, metadata]) => {
    if (metadata.description.toLowerCase().includes(lowerQuery) && !results.find(r => r.name === name)) {
      results.push({ name, ...metadata, score: 70 });
    }
  });
  
  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
};

/**
 * Get function template for insertion
 */
export const getFunctionTemplate = (functionName) => {
  const metadata = DSL_FUNCTION_METADATA[functionName];
  if (!metadata) return functionName + '()';
  
  if (metadata.variadic) {
    return `${functionName}(${metadata.params.join(', ')}, ...)`;
  } else {
    const placeholders = metadata.params.map((param, index) => `${param}`);
    return `${functionName}(${placeholders.join(', ')})`;
  }
};

/**
 * Get all function names for quick lookup
 */
export const getAllFunctionNames = () => {
  return Object.keys(DSL_FUNCTION_METADATA);
};

/**
 * Get function signature with parameter hints
 */
export const getFunctionSignature = (functionName) => {
  const metadata = DSL_FUNCTION_METADATA[functionName];
  if (!metadata) return null;
  
  return {
    name: functionName,
    signature: metadata.signature,
    params: metadata.params,
    description: metadata.description,
    example: metadata.example,
    category: metadata.category
  };
};